import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { ShieldCheck, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const MFA_CODES = ['admin_step_up_required', 'admin_mfa_required'];
const errMsg = (e) => e.response?.data?.detail?.message || e.response?.data?.detail || 'Codice non valido';

const CodeForm = ({ onSubmit, busy, allowRecovery = true, testid = 'mfa' }) => {
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const submit = (e) => { e.preventDefault(); onSubmit(recovery ? { recovery_code: code } : { code }); setCode(''); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Input autoFocus value={code} onChange={e => setCode(recovery ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder={recovery ? 'XXXX-XXXX-XXXX' : '6 cifre'} inputMode={recovery ? 'text' : 'numeric'} className="h-11 text-center tracking-widest" data-testid={`${testid}-code-input`} />
      <Button type="submit" disabled={busy || (!recovery && code.length !== 6) || (recovery && code.length < 8)} className="w-full rounded-full" data-testid={`${testid}-submit`}>Conferma</Button>
      {allowRecovery && <button type="button" onClick={() => setRecovery(!recovery)} className="text-xs text-muted-foreground underline font-sans" data-testid={`${testid}-toggle-recovery`}>
        {recovery ? 'Usa il codice dell\'app' : 'Usa un codice di recupero'}</button>}
    </form>
  );
};

const Enroll = ({ onDone }) => {
  const [data, setData] = useState(null);
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.post(`${API}/admin/mfa/enroll/start`, {}, opts).then(r => setData(r.data)).catch(e => toast.error(errMsg(e))); }, []);
  const confirm = async (body) => {
    setBusy(true);
    try { const r = await axios.post(`${API}/admin/mfa/enroll/confirm`, body, opts); setCodes(r.data.recovery_codes); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (codes) return (
    <div className="space-y-4" data-testid="mfa-recovery-codes">
      <p className="text-sm font-sans">Salva questi <b>codici di recupero</b> in un posto sicuro. Ognuno si usa una sola volta e non verranno più mostrati.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-secondary rounded-lg p-4">{codes.map(c => <span key={c}>{c}</span>)}</div>
      <Button onClick={onDone} className="w-full rounded-full" data-testid="mfa-codes-saved">Ho salvato i codici</Button>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="mfa-enroll">
      <p className="text-sm font-sans">Inquadra il QR code con Google Authenticator, Microsoft Authenticator o Authy, poi inserisci il codice di 6 cifre.</p>
      {data && <div className="flex flex-col items-center gap-2">
        <QRCodeSVG value={data.otpauth_uri} size={180} data-testid="mfa-qr" />
        <code className="text-xs break-all text-muted-foreground" data-testid="mfa-secret">{data.secret}</code>
      </div>}
      <CodeForm onSubmit={confirm} busy={busy} allowRecovery={false} testid="mfa-enroll" />
    </div>
  );
};

export const RegenerateRecoveryCodes = () => {
  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/mfa/verify`, body, opts);
      const r = await axios.post(`${API}/admin/mfa/recovery-codes/regenerate`, {}, opts);
      setCodes(r.data.recovery_codes);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const close = () => { setOpen(false); setCodes(null); };
  return (<>
    <Button variant="outline" onClick={() => setOpen(true)} className="rounded-full btn-lift" data-testid="admin-regenerate-recovery-btn">
      <KeyRound className="w-4 h-4 mr-2" strokeWidth={1.5} /> Rigenera codici di recupero</Button>
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="regenerate-recovery-dialog">
        <DialogHeader><DialogTitle>Nuovi codici di recupero</DialogTitle>
          <DialogDescription>{codes ? 'I codici precedenti non sono più validi.' : 'Inserisci il codice dell\'app: verranno generati 10 nuovi codici e quelli vecchi smetteranno di funzionare.'}</DialogDescription></DialogHeader>
        {codes ? (
          <div className="space-y-4" data-testid="regenerated-recovery-codes">
            <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-secondary rounded-lg p-4">{codes.map(c => <span key={c}>{c}</span>)}</div>
            <Button onClick={close} className="w-full rounded-full" data-testid="regenerated-codes-saved">Ho salvato i codici</Button>
          </div>
        ) : <CodeForm onSubmit={submit} busy={busy} allowRecovery={false} testid="mfa-regenerate" />}
      </DialogContent>
    </Dialog>
  </>);
};

// Intercepts admin 401 (step-up / expired admin session), asks the code, retries the request.
const useStepUpInterceptor = () => {
  const [open, setOpen] = useState(false);
  const pending = useRef([]);
  useEffect(() => {
    const id = axios.interceptors.response.use(null, (error) => {
      const code = error.response?.data?.detail?.code;
      const cfg = error.config || {};
      if (!MFA_CODES.includes(code) || cfg._mfaRetried || (cfg.url || '').includes('/admin/mfa/')) return Promise.reject(error);
      return new Promise((resolve, reject) => { pending.current.push({ cfg, resolve, reject, error }); setOpen(true); });
    });
    return () => axios.interceptors.response.eject(id);
  }, []);
  const finish = (ok) => {
    const list = pending.current; pending.current = []; setOpen(false);
    list.forEach(({ cfg, resolve, reject, error }) => (ok ? axios({ ...cfg, _mfaRetried: true }).then(resolve, reject) : reject(error)));
  };
  return { open, finish };
};

export const AdminMfaGate = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const { open, finish } = useStepUpInterceptor();
  const load = useCallback(() => axios.get(`${API}/admin/mfa/status`, opts).then(r => setStatus(r.data)).catch(() => setStatus({ error: true })), []);
  useEffect(() => { load(); }, [load]);
  const verify = async (body, after) => {
    setBusy(true);
    try { await axios.post(`${API}/admin/mfa/verify`, body, opts); after(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!status) return null;
  if (status.error) return <p className="p-10 text-sm font-sans" data-testid="mfa-error">Accesso Admin non disponibile.</p>;
  if (!status.enrolled || !status.session_verified) return (
    <div className="min-h-[70vh] flex items-center justify-center p-6">
      <Card className="w-full max-w-md rounded-2xl border border-border" data-testid="admin-mfa-gate"><CardContent className="p-8 space-y-5">
        <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" strokeWidth={1.5} /><h1 className="text-lg font-semibold font-sans">Verifica Admin</h1></div>
        {!status.enrolled ? <Enroll onDone={load} /> : (<>
          <p className="text-sm font-sans text-muted-foreground">Inserisci il codice dell'app di autenticazione.</p>
          <CodeForm onSubmit={(b) => verify(b, load)} busy={busy} testid="mfa-verify" />
        </>)}
      </CardContent></Card>
    </div>
  );
  return (<>
    {children}
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(false); }}>
      <DialogContent data-testid="mfa-stepup-dialog">
        <DialogHeader><DialogTitle>Conferma con il codice</DialogTitle>
          <DialogDescription>Per questa operazione serve un codice recente dell'app di autenticazione.</DialogDescription></DialogHeader>
        <CodeForm onSubmit={(b) => verify(b, () => finish(true))} busy={busy} testid="mfa-stepup" />
      </DialogContent>
    </Dialog>
  </>);
};
